"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  NumberInput,
  SelectInput,
  ResultBanner,
  PrintButton,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* Control of Noise at Work Regulations 2005, Schedule 1 Part 1.
   Daily personal noise exposure for a mix of tasks:
     LEP,d = 10·log10( Σᵢ (tᵢ/8)·10^(Lᵢ/10) )
   where tᵢ is hours spent at A-weighted level Lᵢ dB(A), normalised to an
   8-hour reference day. Each task's "partial exposure" in energy terms is
   (tᵢ/8)·10^(Lᵢ/10); summed, then converted back to dB.

   Action values (Reg 4, verified against legislation.gov.uk):
     lower exposure action value (EAV)  = 80 dB(A) LEP,d
     upper exposure action value (EAV)  = 85 dB(A) LEP,d
     exposure limit value (ELV)         = 87 dB(A) LEP,d
   The ELV takes the attenuation of hearing protection into account; the action
   values do not (Reg 4(5)). */

const LOWER_EAV = 80;
const UPPER_EAV = 85;
const ELV = 87;

/* Peak sound pressure action/limit values, dB(C) (Reg 4). */
const PEAK_LOWER = 135;
const PEAK_UPPER = 137;
const PEAK_LIMIT = 140;

/* HSE recommends derating the SNR of hearing protection by 4 dB to allow for
   real-world fit (HSG260 / "Health and safety in noise"): the laboratory SNR
   overstates the protection actually achieved on site. */
const SNR_DERATE = 4;

/* Shared banding of an A-weighted exposure (LEP,d or LEP,w) against the action
   and limit values. Returns the verdict colour, a title suffix and one line of
   guidance — matches the daily-exposure wording. */
function bandExposure(value: number, what: string): { level: Verdict; title: string; advice: string } {
  if (value >= ELV) {
    return {
      level: "veryhigh",
      title: `${value.toFixed(1)} dB(A) ${what} — at or over the exposure limit value`,
      advice:
        "This reaches the exposure limit value of 87 dB(A). The ELV already accounts for the protection given by hearing protectors, so wearing them is not enough — you must change the working arrangement itself until exposure is brought below the limit.",
    };
  }
  if (value >= UPPER_EAV) {
    return {
      level: "high",
      title: `${value.toFixed(1)} dB(A) ${what} — over the upper action value`,
      advice:
        "At or above the upper exposure action value of 85 dB(A) you must reduce exposure as low as reasonably practicable through organisational and technical measures (not just PPE), set up mandatory hearing protection zones, ensure hearing protection is worn, and provide health surveillance for those regularly exposed.",
    };
  }
  if (value >= LOWER_EAV) {
    return {
      level: "medium",
      title: `${value.toFixed(1)} dB(A) ${what} — over the lower action value`,
      advice:
        "At or above the lower exposure action value of 80 dB(A) you must assess the risk, make suitable hearing protection available on request, and give workers information and training on the risk and how to reduce it.",
    };
  }
  return {
    level: "low",
    title: `${value.toFixed(1)} dB(A) ${what} — below the lower action value`,
    advice:
      "This is below the lower exposure action value of 80 dB(A), so the specific duties are not triggered. There is still a general duty to keep noise as low as reasonably practicable.",
  };
}

interface TaskRow {
  id: number;
  name: string;
  level: string; // dB(A)
  hours: string;
  minutes: string;
  peak: string; // dB(C) peak, optional
}

/* Indicative A-weighted levels only — use measured or manufacturer in-use data
   for a real assessment. Figures are typical operator-position estimates. */
const PRESETS: { label: string; db: number }[] = [
  { label: "Disc cutter / cut-off saw (~105 dB)", db: 105 },
  { label: "Breaker / road breaker (~105 dB)", db: 105 },
  { label: "Angle grinder (~100 dB)", db: 100 },
  { label: "Hammer / SDS drill (~100 dB)", db: 100 },
  { label: "Circular saw (~100 dB)", db: 100 },
  { label: "Planer (~95 dB)", db: 95 },
  { label: "Cement / mortar mixer (~90 dB)", db: 90 },
  { label: "General site background (~80 dB)", db: 80 },
];

let nextId = 1;
function emptyRow(): TaskRow {
  return { id: nextId++, name: "", level: "", hours: "", minutes: "", peak: "" };
}

interface RowExposure {
  level: number;
  hours: number;
  /** Partial exposure in energy terms: (t/8)·10^(L/10). */
  energy: number;
  /** This task's standalone LEP,d contribution had it run alone. */
  partialLepd: number;
}

function rowExposure(row: TaskRow): RowExposure | null {
  const level = parseFloat(row.level);
  const h = parseFloat(row.hours || "0");
  const m = parseFloat(row.minutes || "0");
  if (!isFinite(level) || level <= 0) return null;
  const hours = (isFinite(h) ? h : 0) + (isFinite(m) ? m : 0) / 60;
  if (hours <= 0) return null;
  const energy = (hours / 8) * Math.pow(10, level / 10);
  return { level, hours, energy, partialLepd: 10 * Math.log10(energy) };
}

export function NoiseExposureCalculator() {
  const [rows, setRows] = useState<TaskRow[]>([emptyRow()]);
  const [operator, setOperator] = useState("");
  const resultTracked = useRef(false);

  // Feature 1 — weekly average (LEP,w). Up to 7 day-level LEP,d figures dB(A).
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [weekDays, setWeekDays] = useState<string[]>(["", "", "", "", "", "", ""]);

  // Feature 2 — hearing protection check (SNR method).
  const [snr, setSnr] = useState("");
  const [lc, setLc] = useState("");

  const update = (id: number, patch: Partial<TaskRow>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const computed = useMemo(() => {
    const perRow = rows.map((r) => ({ row: r, exp: rowExposure(r) }));
    const valid = perRow.filter((p): p is { row: TaskRow; exp: RowExposure } => p.exp !== null);
    if (valid.length === 0) return null;

    const totalEnergy = valid.reduce((s, p) => s + p.exp.energy, 0);
    const lepd = 10 * Math.log10(totalEnergy);

    let level: Verdict;
    let title: string;
    let advice: string;
    if (lepd >= ELV) {
      level = "veryhigh";
      title = `${lepd.toFixed(1)} dB(A) LEP,d — at or over the exposure limit value`;
      advice =
        "This reaches the exposure limit value of 87 dB(A). The ELV already accounts for the protection given by hearing protectors, so wearing them is not enough — you must change the working arrangement itself: lower-noise methods or equipment, fewer or shorter noisy tasks, screening or distance, and job rotation, until exposure is brought below the limit.";
    } else if (lepd >= UPPER_EAV) {
      level = "high";
      title = `${lepd.toFixed(1)} dB(A) LEP,d — over the upper action value`;
      advice =
        "At or above the upper exposure action value of 85 dB(A) you must reduce exposure as low as reasonably practicable through organisational and technical measures (not just PPE), set up mandatory hearing protection zones, ensure hearing protection is worn, and provide health surveillance (audiometry) for those regularly exposed.";
    } else if (lepd >= LOWER_EAV) {
      level = "medium";
      title = `${lepd.toFixed(1)} dB(A) LEP,d — over the lower action value`;
      advice =
        "At or above the lower exposure action value of 80 dB(A) you must assess the risk, make suitable hearing protection available on request, and give workers information and training on the risk and how to reduce it. Keep noise as low as reasonably practicable.";
    } else {
      level = "low";
      title = `${lepd.toFixed(1)} dB(A) LEP,d — below the lower action value`;
      advice =
        "This is below the lower exposure action value of 80 dB(A), so the specific duties are not triggered. There is still a general duty to keep noise as low as reasonably practicable — and remember this figure is only as good as the levels and durations entered.";
    }
    // Noise dose relative to the upper action value (85 dB(A) over 8h = 100%).
    // "240% of the daily allowance" lands harder than a dB figure.
    const dosePct = (totalEnergy / Math.pow(10, UPPER_EAV / 10)) * 100;

    // Dominant task = largest energy contribution.
    const top = valid.reduce((best, p) => (p.exp.energy > best.exp.energy ? p : best), valid[0]);
    const topId = top.row.id;
    const topPct = totalEnergy > 0 ? Math.round((top.exp.energy / totalEnergy) * 100) : 0;
    const topName = top.row.name || "The unnamed task";

    // Highest peak dB(C) entered against any task, and which action value it trips.
    const peaks = valid
      .map((p) => ({ row: p.row, peak: parseFloat(p.row.peak) }))
      .filter((p) => isFinite(p.peak) && p.peak > 0);
    const maxPeak = peaks.length > 0 ? peaks.reduce((m, p) => (p.peak > m.peak ? p : m), peaks[0]) : null;
    let peakLevel: Verdict | null = null;
    let peakNote = "";
    if (maxPeak) {
      if (maxPeak.peak >= PEAK_LIMIT) {
        peakLevel = "veryhigh";
        peakNote = `Peak of ${maxPeak.peak} dB(C) is at or over the peak exposure limit value (140 dB(C)) — the working method must change; hearing protection alone is not enough at the limit.`;
      } else if (maxPeak.peak >= PEAK_UPPER) {
        peakLevel = "high";
        peakNote = `Peak of ${maxPeak.peak} dB(C) is at or over the upper peak action value (137 dB(C)) — mandatory hearing protection and health surveillance apply, and reduce the peak at source.`;
      } else if (maxPeak.peak >= PEAK_LOWER) {
        peakLevel = "medium";
        peakNote = `Peak of ${maxPeak.peak} dB(C) is at or over the lower peak action value (135 dB(C)) — make hearing protection available and assess the impulsive noise.`;
      }
    }

    return { perRow: valid, totalEnergy, lepd, dosePct, level, title, advice, topId, topPct, topName, maxPeak, peakLevel, peakNote };
  }, [rows]);

  // Weekly average LEP,w (Schedule 1 Part 2). The divisor is always 5 — it is a
  // 5-day reference week, not the number of days worked.
  const weekly = useMemo(() => {
    const days = weekDays
      .map((d) => parseFloat(d))
      .filter((d) => isFinite(d) && d > 0);
    if (days.length === 0) return null;
    const energy = days.reduce((s, d) => s + Math.pow(10, d / 10), 0) / 5;
    const lepw = 10 * Math.log10(energy);
    return { count: days.length, lepw, ...bandExposure(lepw, "LEP,w") };
  }, [weekDays]);

  // Hearing protection check (HSE SNR method). Real-world derate of 4 dB applied
  // to the SNR. L'A = LC − (SNR − 4). LC measured if given, else estimated from
  // the daily A-weighted figure (LC ≈ LEP,d + 2).
  const protection = useMemo(() => {
    const snrN = parseFloat(snr);
    if (!isFinite(snrN) || snrN <= 0) return null;
    const lcN = parseFloat(lc);
    const lcMeasured = isFinite(lcN) && lcN > 0;
    const lcEstimated = computed ? computed.lepd + 2 : null;
    const lcUsed = lcMeasured ? lcN : lcEstimated;
    if (lcUsed === null) return null; // nothing to work from yet
    const effectiveSnr = snrN - SNR_DERATE;
    const protectedLevel = lcUsed - effectiveSnr;

    let level: Verdict;
    let title: string;
    let advice: string;
    if (protectedLevel > 85) {
      level = "veryhigh";
      title = `Protected level ${protectedLevel.toFixed(0)} dB(A) — not sufficient`;
      advice =
        "Above 85 dB(A) at the ear, this protector is not giving enough attenuation. Choose a higher-attenuation (higher-SNR) protector or, better, reduce the noise at source so less attenuation is needed.";
    } else if (protectedLevel < 70) {
      level = "medium";
      title = `Protected level ${protectedLevel.toFixed(0)} dB(A) — possible over-protection`;
      advice =
        "Below 70 dB(A) at the ear can over-protect: the wearer feels isolated and may not hear alarms, warnings, vehicles or instructions. Consider a lower-attenuation protector so they stay aware of their surroundings.";
    } else {
      level = "low";
      title = `Protected level ${protectedLevel.toFixed(0)} dB(A) — about right`;
      advice =
        "A protected level of 70–80 dB(A) at the ear is the target band under HSE guidance: enough protection without cutting the wearer off from alarms and warnings.";
    }
    return { snr: snrN, effectiveSnr, lcUsed, lcMeasured, protectedLevel, level, title, advice };
  }, [snr, lc, computed]);

  // Fire the funnel event once per visit when a result first appears.
  useEffect(() => {
    if (computed && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("noise-exposure-calculator", { lepd: Math.round(computed.lepd * 10) / 10 });
    }
  }, [computed]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Inputs */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Noisy tasks in a day</h2>
        <p className="mt-1 text-sm text-muted">
          Enter each noisy task, the A-weighted sound level in dB(A) and how long the worker actually spends on
          it. The calculator combines them into one daily exposure (LEP,d) normalised to an 8-hour day.
        </p>

        <div className="mt-5 space-y-5">
          {rows.map((row, i) => (
            <div key={row.id} className="rounded-xl border border-line bg-mist/60 p-4">
              <div className="flex items-center justify-between">
                <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Task {i + 1}</span>
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setRows((rs) => rs.filter((r) => r.id !== row.id))}
                    className="text-[0.8rem] font-semibold text-muted hover:text-risk-veryhigh"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Field label="Task or tool" className="sm:col-span-2">
                  <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                    <TextInput
                      value={row.name}
                      onChange={(e) => update(row.id, { name: e.target.value })}
                      placeholder="e.g. cutting kerbs with a disc cutter"
                    />
                    <SelectInput
                      aria-label="Indicative noise levels"
                      value=""
                      onChange={(e) => {
                        const preset = PRESETS[parseInt(e.target.value, 10)];
                        if (preset)
                          update(row.id, { name: preset.label.replace(/ \(.*\)$/, ""), level: String(preset.db) });
                      }}
                    >
                      <option value="">Indicative levels…</option>
                      {PRESETS.map((p, idx) => (
                        <option key={p.label} value={idx}>{p.label}</option>
                      ))}
                    </SelectInput>
                  </div>
                </Field>
                <Field label="Sound level dB(A)" hint="Use measured or manufacturer in-use data where you can.">
                  <NumberInput
                    min={0}
                    step={1}
                    value={row.level}
                    onChange={(e) => update(row.id, { level: e.target.value })}
                    placeholder="e.g. 100"
                  />
                </Field>
                <Field label="Time on this task">
                  <div className="flex items-center gap-2">
                    <NumberInput min={0} step={1} value={row.hours} onChange={(e) => update(row.id, { hours: e.target.value })} placeholder="0" aria-label="Hours" />
                    <span className="text-sm text-muted">h</span>
                    <NumberInput min={0} step={5} value={row.minutes} onChange={(e) => update(row.id, { minutes: e.target.value })} placeholder="0" aria-label="Minutes" />
                    <span className="text-sm text-muted">min</span>
                  </div>
                </Field>
                <Field label="Peak dB(C)" hint="Optional — for impulsive/impact noise (cartridge tools, hammering). Checked against the 135/137/140 dB(C) peak values.">
                  <NumberInput
                    min={0}
                    step={1}
                    value={row.peak}
                    onChange={(e) => update(row.id, { peak: e.target.value })}
                    placeholder="e.g. 137"
                  />
                </Field>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setRows((rs) => [...rs, emptyRow()])}
            className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
          >
            + Add another task
          </button>
          <Field label="" className="min-w-[200px] flex-1">
            <TextInput value={operator} onChange={(e) => setOperator(e.target.value)} placeholder="Worker / operator name (for the printed record)" />
          </Field>
        </div>

        <p className="mt-4 rounded-lg bg-mist px-3.5 py-3 text-[0.82rem] leading-relaxed text-muted">
          Doubling rule: noise is logarithmic, so <strong>+3 dB(A) doubles the sound energy</strong> — and halves
          the time you can be exposed for the same daily dose. Two tasks at the same level for the same time add
          3 dB, not double the number.
        </p>
      </div>

      {/* Feature 1 — weekly average (LEP,w) */}
      <div className="card shadow-soft p-6 lg:col-start-1">
        <button
          type="button"
          onClick={() => setWeeklyOpen((o) => !o)}
          aria-expanded={weeklyOpen}
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <span>
            <span className="block text-xl">Weekly average (LEP,w)</span>
            <span className="mt-0.5 block text-sm text-muted">
              Optional — for workers whose daily exposure varies markedly between days.
            </span>
          </span>
          <svg
            viewBox="0 0 24 24"
            className={`h-5 w-5 shrink-0 text-muted transition-transform ${weeklyOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        {weeklyOpen && (
          <div className="mt-5">
            <p className="rounded-lg bg-mist px-3.5 py-3 text-[0.82rem] leading-relaxed text-muted">
              Enter each day&apos;s LEP,d for the week. The weekly average always divides by{" "}
              <strong>5</strong> — it is a 5-day reference week, regardless of how many days were actually
              worked (Control of Noise at Work Regulations 2005, Schedule 1 Part 2). Weekly averaging is only
              appropriate where daily exposure varies markedly between days.
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {weekDays.map((d, i) => (
                <Field key={i} label={`Day ${i + 1} LEP,d dB(A)`}>
                  <div className="flex items-center gap-2">
                    <NumberInput
                      min={0}
                      step={1}
                      value={d}
                      onChange={(e) =>
                        setWeekDays((ds) => ds.map((v, j) => (j === i ? e.target.value : v)))
                      }
                      placeholder={i === 0 ? "e.g. 88" : "—"}
                    />
                    {i === 0 && computed && (
                      <button
                        type="button"
                        onClick={() =>
                          setWeekDays((ds) =>
                            ds.map((v, j) => (j === 0 ? computed.lepd.toFixed(1) : v)),
                          )
                        }
                        className="shrink-0 whitespace-nowrap rounded-full border border-line-2 px-3 py-1.5 text-[0.78rem] font-semibold text-ink hover:border-ink hover:bg-mist"
                      >
                        Use today&apos;s result
                      </button>
                    )}
                  </div>
                </Field>
              ))}
            </div>

            {weekly && (
              <div className="mt-4">
                <ResultBanner level={weekly.level} title={weekly.title}>
                  {weekly.advice}
                </ResultBanner>
                <p className="mt-2 text-[0.78rem] leading-relaxed text-muted">
                  Averaged over a 5-day reference week from {weekly.count} day
                  {weekly.count === 1 ? "" : "s"} entered. Weekly averaging is only appropriate where daily
                  exposure varies markedly between days — where days are similar, use the daily LEP,d.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Feature 2 — hearing protection check (HSE SNR method) */}
      <div className="card shadow-soft p-6 lg:col-start-1">
        <h2 className="text-xl">Will your hearing protection be enough?</h2>
        <p className="mt-1 text-sm text-muted">
          Checks a protector against the level at the ear using the HSE SNR method. The target at the ear is
          70–80 dB(A): protected but still able to hear alarms and warnings.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Protector SNR (dB)" hint="Single Number Rating from the packaging or data sheet.">
            <NumberInput
              min={0}
              step={1}
              value={snr}
              onChange={(e) => setSnr(e.target.value)}
              placeholder="e.g. 30"
            />
          </Field>
          <Field
            label="Measured level LC dB(C)"
            hint="Optional. If you only know the A-weighted level, LC is typically a couple of dB higher for broadband site noise — a measured LC is better. Left blank, it's estimated from the daily figure."
          >
            <NumberInput
              min={0}
              step={1}
              value={lc}
              onChange={(e) => setLc(e.target.value)}
              placeholder="e.g. 102"
            />
          </Field>
        </div>

        <p className="mt-4 rounded-lg bg-mist px-3.5 py-3 text-[0.82rem] leading-relaxed text-muted">
          HSE recommends derating the protection by <strong>4 dB</strong> to allow for real-world fit — the
          laboratory SNR overstates what&apos;s achieved on site. This calculation applies that 4 dB derate:
          protected level = LC − (SNR − 4).
        </p>

        {protection && (
          <div className="mt-4">
            <ResultBanner level={protection.level} title={protection.title}>
              {protection.advice}
            </ResultBanner>
            <p className="mt-2 text-[0.78rem] leading-relaxed text-muted">
              Using {protection.lcMeasured ? "your measured" : "an estimated"} LC of{" "}
              {protection.lcUsed.toFixed(0)} dB(C)
              {protection.lcMeasured ? "" : " (≈ LEP,d + 2 — an estimate, a measured LC is better)"} and a
              derated SNR of {protection.effectiveSnr.toFixed(0)} dB (SNR {protection.snr.toFixed(0)} − 4 dB
              real-world).
            </p>
          </div>
        )}
      </div>

      {/* Result + printable record */}
      <div className="space-y-4">
        {computed ? (
          <>
            <div className="print-area card shadow-soft p-6" id="noise-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Daily noise exposure record
              </div>
              <h2 className="mt-1 text-lg">
                {operator ? `${operator} — ` : ""}
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </h2>

              <div className="mt-4">
                <ResultBanner level={computed.level} title={computed.title}>
                  {computed.advice}
                </ResultBanner>
              </div>

              <table className="mt-4 w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-[0.78rem] uppercase tracking-wide text-muted">
                    <th className="py-1.5 pr-2 font-semibold">Task</th>
                    <th className="py-1.5 pr-2 font-semibold">dB(A)</th>
                    <th className="py-1.5 pr-2 font-semibold">Time</th>
                    <th className="py-1.5 text-right font-semibold">Contribution</th>
                  </tr>
                </thead>
                <tbody>
                  {computed.perRow.map(({ row, exp }) => {
                    const isTop = row.id === computed.topId && computed.perRow.length > 1;
                    return (
                    <tr key={row.id} className={`border-b border-line/60 ${isTop ? "bg-emerald-tint" : ""}`}>
                      <td className="py-2 pr-2 text-ink">
                        {row.name || "Unnamed task"}
                        {isTop && (
                          <span className="ml-1.5 rounded-full bg-emerald-deep px-1.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-white">loudest</span>
                        )}
                      </td>
                      {/* colour-coded against the action values at source level */}
                      <td className={`py-2 pr-2 ${exp.level >= UPPER_EAV ? "font-semibold text-[#b34e10]" : exp.level >= LOWER_EAV ? "font-semibold text-[#8a6400]" : ""}`}>{exp.level}</td>
                      <td className="py-2 pr-2">
                        {Math.floor(exp.hours)}h {Math.round((exp.hours % 1) * 60)}m
                      </td>
                      <td className={`py-2 text-right font-semibold ${isTop ? "text-emerald-deep" : "text-ink"}`}>{exp.partialLepd.toFixed(1)}</td>
                    </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={3} className="py-2 pr-2 font-semibold text-ink">Daily exposure LEP,d</td>
                    <td className="py-2 text-right font-display text-lg font-bold text-ink">{computed.lepd.toFixed(1)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-1 text-[0.72rem] leading-relaxed text-muted">
                Per-task contribution is each task&apos;s own LEP,d had it run alone; tasks combine by energy, not
                by adding the decibels.
              </p>

              {/* Dose: % of the 85 dB(A)/8h daily allowance used. */}
              <p className="mt-3 rounded-lg bg-mist px-3 py-2 text-[0.8rem] leading-relaxed text-muted">
                This mix uses{" "}
                <strong className={computed.dosePct >= 100 ? "text-[#b34e10]" : "text-ink"}>
                  {computed.dosePct >= 10 ? Math.round(computed.dosePct) : computed.dosePct.toFixed(1)}%
                </strong>{" "}
                of the daily noise allowance at the upper action value (85 dB(A) over 8 hours).
              </p>

              {/* Loudest single contributor — change this task first. */}
              {computed.perRow.length > 1 && (
                <p className="mt-3 rounded-lg border border-emerald/30 bg-emerald-tint px-3 py-2 text-[0.8rem] leading-relaxed text-emerald-deep">
                  <strong>{computed.topName}</strong> is driving <strong>{computed.topPct}%</strong> of today&apos;s
                  noise energy — quieter methods or less time on that task cut the dose fastest.
                </p>
              )}

              {/* Peak dB(C) warning against the peak action/limit values. */}
              {computed.peakLevel && (
                <div className="mt-3">
                  <ResultBanner level={computed.peakLevel} title={`Peak noise ${computed.maxPeak!.peak} dB(C)`}>
                    {computed.peakNote} The peak action and limit values (135 / 137 / 140 dB(C)) are separate from
                    the daily LEP,d above and apply to impulsive, impact and explosive noise.
                  </ResultBanner>
                </div>
              )}

              {/* Weekly average (LEP,w) — only when days have been entered. */}
              {weekly && (
                <div className="mt-3">
                  <ResultBanner level={weekly.level} title={weekly.title}>
                    {weekly.advice} Averaged over a 5-day reference week from {weekly.count} day
                    {weekly.count === 1 ? "" : "s"} entered (Schedule 1 Part 2). Weekly averaging is only
                    appropriate where daily exposure varies markedly between days.
                  </ResultBanner>
                </div>
              )}

              {/* Hearing protection check (SNR method) — only when an SNR is given. */}
              {protection && (
                <div className="mt-3">
                  <ResultBanner level={protection.level} title={protection.title}>
                    {protection.advice} Based on {protection.lcMeasured ? "a measured" : "an estimated"} LC of{" "}
                    {protection.lcUsed.toFixed(0)} dB(C)
                    {protection.lcMeasured ? "" : " (≈ LEP,d + 2)"} and a derated SNR of{" "}
                    {protection.effectiveSnr.toFixed(0)} dB (SNR {protection.snr.toFixed(0)} − 4 dB applied for
                    real-world fit, per HSE guidance).
                  </ResultBanner>
                </div>
              )}

              <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[0.78rem]">
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-bold text-ink">{LOWER_EAV} dB</div>
                  <div className="text-muted">lower action value</div>
                </div>
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-bold text-ink">{UPPER_EAV} dB</div>
                  <div className="text-muted">upper action value</div>
                </div>
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-bold text-ink">{ELV} dB</div>
                  <div className="text-muted">limit value</div>
                </div>
              </div>

              <PrintFooter toolName="noise exposure calculator" />
            </div>
            <div className="flex justify-end">
              <PrintButton tool="noise-exposure-calculator" label="Print / save record as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Add a task with a sound level in dB(A) and how long it lasts to see the daily exposure (LEP,d) and how
            it compares to the lower (80 dB) and upper (85 dB) action values and the exposure limit value (87 dB).
          </div>
        )}
      </div>
    </div>
  );
}
